import { KebabDining, Person, ReceiptLong } from "@mui/icons-material";
import {
  Box,
  CssBaseline,
  Drawer,
  Fab,
  Link,
  ThemeProvider,
  Typography
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import axios from "axios";
import Cookies from "js-cookie";
import { useEffect, useState } from "react";
import PizzaHubLogo from "./assets/pizza_hub_logo.svg";
import PizzaHubLogoLight from "./assets/pizza_hub_logo_light.png";
import { OrDivider } from "./components/Dividers";
import { MyButton } from "./components/MyButton";
import { PizzaOrdering } from "./components/PizzaOrdering";
import { AllowNotify } from "./components/PushNotify";
import { Profile } from "./components/User";
import { AdminPanel } from "./components/admin/AdminPanel";
import { CustomerLogin } from "./components/auth/CustomerLogin";
import { OpencampLogin } from "./components/auth/OpencampLogin";
import { SERVER_API_PREFIX, darkMode } from "./constants";
import { theme } from "./theme";
import { TCustomer, THungerGame, TUser } from "./types/AllTypes";

axios.defaults.withCredentials = true;
axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";

function App() {
  const [user, setUser] = useState<TUser | undefined>(undefined);
  const [customer, setCustomer] = useState<TCustomer | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState<boolean>(false);
  const [hungerGame, setHungerGame] = useState<THungerGame | null>(null);
  const [isAdminPage, setIsAdminPage] = useState<boolean | undefined>(
    undefined
  );
  const [accountOpen, setAccountOpen] = useState(false);
  const [allowLeave, setAllowLeave] = useState(true);
  const [notificationAllowed, setNotificationAllowed] = useState(false);
  const canOrder = hungerGame && hungerGame.deadline.getTime() > Date.now();

  function Logo() {
    if (darkMode) {
      return <img src={PizzaHubLogo} alt="Pizza hub logo" />;
    } else {
      return <img src={PizzaHubLogoLight} alt="Pizza hub logo" />;
    }
  }
  const fetchHG = async (changeAdminPage: boolean) => {
    fetchUser();
    axios
      .get<THungerGame>(`${SERVER_API_PREFIX}/hunger_game`)
      .then(response => {
        const hungerGame =
          Object.keys(response.data).length === 0
            ? null
            : ({
                ...response.data,
                eta: new Date(response.data.eta),
                deadline: new Date(response.data.deadline)
              } as THungerGame);
        setHungerGame(hungerGame);

        setLoading(false);
        setConnected(true);
        if (changeAdminPage) {
          setLocalIsAdminPage(
            hungerGame ? hungerGame.deadline.getTime() < Date.now() : true
          );
        }
        getNotificationPermission();
      })
      .catch(error => {
        console.error("Error fetching hunger game:", error);
        setLoading(false);
        setHungerGame(null);
        setConnected(false);
      });
  };

  const initializePush = () => {
    axios.get<string>(`${SERVER_API_PREFIX}/vapidPublicKey`).then(res => {
      const publicKey = res.data;
      navigator.serviceWorker.ready.then(registration => {
        registration.pushManager
          .subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(publicKey)
          })
          .then(subscription => {
            console.log("User is subscribed:", subscription);
            axios.put(`${SERVER_API_PREFIX}/subscribe`, {
              subscription: JSON.stringify(subscription)
            });
          })
          .catch(error => {
            console.error("Failed to subscribe the user:", error);
          });
      });
    });
  };

  function urlBase64ToUint8Array(base64String: string) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, "+")
      .replace(/_/g, "/");
    const rawData = window.atob(base64);
    return new Uint8Array([...rawData].map(char => char.charCodeAt(0)));
  }

  const getNotificationPermission = () => {
    if ("Notification" in window && navigator.serviceWorker) {
      Notification.requestPermission(status => {
        console.log("Notification permission status:", status);
        if (status === "granted") {
          setNotificationAllowed(true);
          initializePush();
        } else {
          setNotificationAllowed(false);
        }
      });
    }
  };

  // CUSTOMER

  const saveCustomer = (customer: TCustomer) => {
    setCustomer(customer);
    Cookies.set("customer", JSON.stringify(customer));
  };

  const logoutCustomer = () => {
    if (
      window.confirm(
        "Naozaj sa chcete odhlasit? Uz nebudete moct upravit/zrusit objednavku."
      )
    ) {
      const cookiecustomer = Cookies.get("customer");
      if (cookiecustomer) {
        Cookies.remove("customer");
        window.location.reload();
      }
    }
  };

  const fetchUser = () => {
    axios.get<{ user: TUser }>(SERVER_API_PREFIX + "/me").then(response => {
      if (response.data.user) {
        setUser({
          ...response.data.user,
          wantPizzaFrom: response.data.user.wantPizzaFrom
            ? new Date(response.data.user.wantPizzaFrom)
            : null,
          restrictedUntil: response.data.user.restrictedUntil
            ? new Date(response.data.user.restrictedUntil)
            : null
        });
        console.log("Logged in user:", response.data);
      } else {
        const cookiecustomer = Cookies.get("customer");
        if (cookiecustomer) {
          setCustomer(JSON.parse(cookiecustomer));
        }
      }
    });
  };

  const getLocalIsAdminPage = () => {
    const cookieAdminPage = Cookies.get("isAdminPage");
    if (cookieAdminPage) {
      setIsAdminPage(cookieAdminPage === "true");
      return cookieAdminPage === "true";
    }
    return undefined;
  };

  const setLocalIsAdminPage = (isLocalAdminPage: boolean) => {
    Cookies.set("isAdminPage", isLocalAdminPage.toString());
    setIsAdminPage(isLocalAdminPage);
  };

  useEffect(() => {
    axios
      .get<{ csrf_token: string }>(`${SERVER_API_PREFIX}/csrf`)
      .then(res => {
        axios.defaults.headers.common["X-CSRF-Token"] = res.data.csrf_token;
      })
      .catch(() => {
        setConnected(false);
      })
      .then(() => {
        fetchHG(getLocalIsAdminPage() === undefined);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
      meta.setAttribute("content", theme.palette.background.default);
    }
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <CssBaseline />
        <Box padding={1.5}>
          <Box justifyContent={"center"} display={"flex"} padding={2}>
            <Link href={window.location.origin}>
              <Logo></Logo>
            </Link>
          </Box>
          {!loading &&
            (connected ? (
              <>
                {(customer || user) && (
                  <Fab
                    sx={{
                      position: "fixed",
                      top: 10,
                      left: 10
                    }}
                    size="small"
                    color="primary"
                    aria-label="open account page"
                    onClick={() => setAccountOpen(!accountOpen)}
                  >
                    <Person color="action" />
                  </Fab>
                )}
                {user && user.isAdmin && allowLeave && (
                  <>
                    <Fab
                      sx={{
                        position: "fixed",
                        top: 60,
                        left: 10
                      }}
                      size="small"
                      color="info"
                      aria-label="switch order-admin mode"
                      onClick={() => {
                        fetchHG(false);
                        setLocalIsAdminPage(!isAdminPage);
                      }}
                    >
                      {isAdminPage ? (
                        <KebabDining color="action" />
                      ) : (
                        <ReceiptLong color="action" />
                      )}
                    </Fab>
                  </>
                )}
                {user && user.isAdmin && isAdminPage ? (
                  <AdminPanel
                    currentUser={user}
                    canOrder={!!canOrder}
                    setAllowLeave={setAllowLeave}
                  />
                ) : (
                  <>
                    {hungerGame && !hungerGame.finished ? (
                      <Box>
                        {user || customer ? (
                          <>
                            {!notificationAllowed && (
                              <AllowNotify
                                text="Chces byt prvy v rade ked pride pizza?"
                                onClick={getNotificationPermission}
                              />
                            )}
                            <PizzaOrdering
                              username={
                                user
                                  ? `${user.first_name} ${user.last_name} ${user.phone}`
                                  : `${customer?.meno} ${customer?.cislo}`
                              }
                              isRestricted={
                                user && user.restrictedUntil
                                  ? user.restrictedUntil > new Date()
                                  : false
                              }
                              customer={customer}
                              canOrder={!!canOrder}
                              hungerGame={hungerGame}
                            />
                          </>
                        ) : (
                          <Box
                            display={"flex"}
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                          >
                            {hungerGame.deadline.getTime() > Date.now() ? (
                              <>
                                <Typography
                                  variant="h4"
                                  sx={{ textAlign: "center" }}
                                  marginBottom={5}
                                >
                                  Prihlaste sa pre objednavku pizze
                                </Typography>
                                {hungerGame.allowGuests && (
                                  <>
                                    <CustomerLogin
                                      saveCustomer={saveCustomer}
                                    />
                                    <OrDivider text="alebo" />
                                  </>
                                )}
                              </>
                            ) : (
                              <>
                                {!notificationAllowed && (
                                  <AllowNotify
                                    text="Chces si nabuduce stihnut objednat vcas?"
                                    onClick={getNotificationPermission}
                                  />
                                )}
                                <Typography
                                  variant="h4"
                                  sx={{ textAlign: "center" }}
                                  marginBottom={5}
                                >
                                  Objednavanie uz skoncilo
                                </Typography>
                              </>
                            )}
                            <OpencampLogin />
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <>
                        {!notificationAllowed && (
                          <AllowNotify
                            text="Chces si nabuduce stihnut objednat vcas?"
                            onClick={getNotificationPermission}
                          />
                        )}
                        <Box
                          display={"flex"}
                          flexDirection={"column"}
                          alignItems={"center"}
                          justifyContent={"center"}
                        >
                          <Typography
                            variant="h4"
                            marginBottom={5}
                            sx={{ textAlign: "center" }}
                          >
                            Momentalne sa neobjednava. Skuste znova neskor
                          </Typography>
                          {!user && !customer && <OpencampLogin />}
                          <MyButton
                            variant="contained"
                            sx={{
                              margin: 2,
                              fontSize: 20,
                              backgroundColor:
                                (user != undefined &&
                                  user.wantPizzaFrom != null) ||
                                user == undefined
                                  ? "action.disabledBackground"
                                  : "primary.main"
                            }}
                            disabled={
                              user != undefined && user.wantPizzaFrom != null
                            }
                            onClick={() => {
                              if (user) {
                                if (user.wantPizzaFrom == null) {
                                  axios
                                    .post(`${SERVER_API_PREFIX}/wantpizza`)
                                    .then(() => {
                                      setUser({
                                        ...user,
                                        wantPizzaFrom: new Date()
                                      });
                                    });
                                } else {
                                  alert("Uz si zahlasoval");
                                }
                              } else {
                                alert("Najprv sa prihlas cez Opencamp");
                              }
                            }}
                          >
                            Chcem pizzu!
                          </MyButton>
                        </Box>
                      </>
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                <Box
                  display={"flex"}
                  alignItems={"center"}
                  justifyContent={"center"}
                  flexDirection={"column"}
                >
                  <Typography variant="h4" sx={{ textAlign: "center" }}>
                    Nepodarilo sa pripojit k serveru. Prosim skontrolujte
                    pripojenie k internetu a skuste to znova.
                  </Typography>
                  <MyButton
                    onClick={() => {
                      setLoading(true);
                      fetchHG(true);
                    }}
                    variant="contained"
                    sx={{ margin: 2, fontSize: 20 }}
                  >
                    Skusit znova
                  </MyButton>
                </Box>
              </>
            ))}
          <footer>
            <Box position={"fixed"} bottom={5} right={10} fontSize={10}>
              Since 2023
            </Box>
          </footer>
          <Drawer //account
            anchor={"bottom"}
            open={accountOpen && (user != undefined || customer != undefined)}
            onClose={() => {
              setAccountOpen(false);
            }}
          >
            <Box height={"80vh"}>
              <Profile
                user={user}
                customer={customer}
                logoutCustomer={logoutCustomer}
                allowLeave={allowLeave}
              />
            </Box>
          </Drawer>
        </Box>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default App;

// by Miloš Vasiľ, Lukáš Paugsch, Dávid Vadkerti, Samuel Chovanec, Valter Martinek
